import { Search, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { Profile } from '../types';
import { getSupabase } from '../lib/supabase';
import Avatar from './Avatar';

export default function GroupChatModal({
  currentProfile,
  onClose,
  onCreated,
}: {
  currentProfile: Profile;
  onClose: () => void;
  onCreated: (conversationId: string) => void;
}) {
  const [groupName, setGroupName] = useState('');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Profile[]>([]);
  const [selected, setSelected] = useState<Profile[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      return;
    }

    const timeout = window.setTimeout(async () => {
      const client = getSupabase();
      const term = query.trim().replace(/[%_]/g, '');
      const { data } = await client
        .from('profiles')
        .select('*')
        .or(`username.ilike.%${term}%,display_name.ilike.%${term}%`)
        .limit(12);

      setResults(
        ((data ?? []) as Profile[]).filter(
          (profile) => profile.id !== currentProfile.id && !selected.some((member) => member.id === profile.id),
        ),
      );
    }, 250);

    return () => window.clearTimeout(timeout);
  }, [currentProfile.id, query, selected]);

  async function createGroup() {
    if (!groupName.trim()) {
      setError('Give the group a name.');
      return;
    }

    const client = getSupabase();
    setCreating(true);
    setError(null);

    const { data, error: createError } = await client.rpc('create_group_conversation', {
      group_name: groupName.trim(),
      member_ids: selected.map((profile) => profile.id),
    });

    setCreating(false);

    if (createError) {
      setError(createError.message);
      return;
    }

    onCreated(data);
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 px-4 backdrop-blur-sm">
      <section className="w-full max-w-lg rounded-xl border border-white/10 bg-ink-850 p-5 shadow-panel">
        <div className="mb-5 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-white">New group chat</h2>
            <p className="text-sm text-slate-500">Create a named room and invite members.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="grid h-9 w-9 place-items-center rounded-lg text-slate-400 transition hover:bg-white/10 hover:text-white"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <label className="block">
          <span className="text-sm font-medium text-slate-300">Group name</span>
          <input
            value={groupName}
            onChange={(event) => setGroupName(event.target.value)}
            className="mt-2 w-full rounded-lg border border-white/10 bg-black/25 px-4 py-3 text-sm text-white"
            placeholder="Design Team"
          />
        </label>

        <label className="relative mt-4 block">
          <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            className="w-full rounded-lg border border-white/10 bg-black/25 py-3 pl-10 pr-4 text-sm text-white"
            placeholder="Search users"
          />
        </label>

        {selected.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {selected.map((profile) => (
              <button
                key={profile.id}
                type="button"
                onClick={() => setSelected((current) => current.filter((member) => member.id !== profile.id))}
                className="inline-flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.04] px-2 py-1 text-xs text-slate-200"
              >
                {profile.display_name}
                <X size={13} />
              </button>
            ))}
          </div>
        )}

        {results.length > 0 && (
          <div className="relay-scrollbar mt-4 max-h-48 overflow-y-auto rounded-lg border border-white/10">
            {results.map((profile) => (
              <button
                key={profile.id}
                type="button"
                onClick={() => {
                  setSelected((current) => [...current, profile]);
                  setQuery('');
                }}
                className="flex w-full items-center gap-3 border-b border-white/10 px-3 py-2 text-left last:border-b-0 hover:bg-white/[0.04]"
              >
                <Avatar name={profile.display_name} src={profile.avatar_url} />
                <span>
                  <span className="block text-sm font-semibold text-white">{profile.display_name}</span>
                  <span className="text-xs text-slate-500">@{profile.username}</span>
                </span>
              </button>
            ))}
          </div>
        )}

        {error && <p className="mt-4 rounded-lg bg-relay-red/10 p-3 text-sm text-relay-red">{error}</p>}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-white/10 px-4 py-2 text-sm font-semibold text-slate-300"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={createGroup}
            disabled={creating}
            className="rounded-lg bg-relay-blue px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
          >
            {creating ? 'Creating...' : 'Create group'}
          </button>
        </div>
      </section>
    </div>
  );
}

